import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import SoftProfile from "../../ProfilePictures/SoftProfile";
import { formatNumber } from "../../../helpers/formatter";
import { formatKpiPercent } from "../../../helpers/scoreAggregate";
import { resolveMonthKpiKey } from "../../../constants/kpi";

// Imported Images and Icons
import goldRank from "../../../img/lb-gold.png";
import silverRank from "../../../img/lb-silver.png";
import bronzeRank from "../../../img/lb-bronze.png";

const RANK_BADGES = [goldRank, silverRank, bronzeRank];

const colMinWidths = (isSmall) => ({
  rank: isSmall ? "1.75rem" : "2rem",
  player: isSmall ? "5rem" : "7rem",
  workspace: isSmall ? "4rem" : "6rem",
  total: isSmall ? "3.25rem" : "4.5rem",
  kpi: isSmall ? "3.25rem" : "4.5rem",
});

const getValueFontSize = (isSmall, isTotal) => {
  if (isSmall) return isTotal ? "14px" : "13px";
  return isTotal ? "20px" : "16px";
};

const valueTextSx = (isSmall, isTotal = false) => ({
  fontFamily: "DM Sans",
  fontWeight: 600,
  fontSize: getValueFontSize(isSmall, isTotal),
  lineHeight: isTotal ? "24px" : "20px",
  color: "#343A40",
  whiteSpace: "nowrap",
});

// Use the KPI list from the parent (same as Scorekeeper column order/names).
function getVisibleKpis(groupKpis, players, kpiCatalog, metricsReady) {
  if (groupKpis?.length) return groupKpis;
  if (!metricsReady || !players?.length) return [];

  const kpiIds = new Set();
  players.forEach((p) => {
    Object.keys(p.scores || {}).filter((k) => k !== "total").forEach((id) => kpiIds.add(id));
  });

  return Array.from(kpiIds)
    .map((_id) => {
      const kpi = (kpiCatalog || []).find((k) => String(k._id) === String(_id));
      if (!kpi?.title) return null;
      return { _id, title: kpi.title, kind: kpi.kind ?? "number" };
    })
    .filter(Boolean);
}

function scoreAmtForKpi(playerScores, kpiId) {
  if (!playerScores || kpiId == null) return 0;
  const key = resolveMonthKpiKey(playerScores, kpiId);
  return playerScores[key]?.amt ?? 0;
}

const CompanyTopPerformers = ({
  groupKpis,
  usersKpis,
  kpiCatalog = [],
  metricsReady = true,
  bigMode = true,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const widths = colMinWidths(isSmallScreen);
  const players = Array.isArray(usersKpis) ? usersKpis : [];

  const visibleKpis = useMemo(
    () => getVisibleKpis(groupKpis, players, kpiCatalog, metricsReady),
    [groupKpis, players, kpiCatalog, metricsReady],
  );

  const kpiHeaders = useMemo(
    () =>
      visibleKpis.map((kpi) => ({
        id: String(kpi._id),
        label: typeof kpi.title === "string" && kpi.title ? kpi.title : "",
      })),
    [visibleKpis],
  );

  const kpiScores = useMemo(() => {
    if (!players.length) return [];
    return players.map((player) => ({
      ...player,
      kpiAmounts: visibleKpis.map((gKpi) => ({
        id: gKpi._id,
        amt: scoreAmtForKpi(player.scores, gKpi._id),
        kind: gKpi.kind ?? "number",
        title: gKpi.title,
      })),
    }));
  }, [players, visibleKpis]);

  return (
    <div
      className="overflow-x-auto min-w-0"
      style={{
        WebkitOverflowScrolling: "touch",
        minHeight: 0,
      }}
    >
      <table
        className="min-w-max w-full"
        style={{ borderCollapse: "separate", borderSpacing: "0 1rem", tableLayout: "auto" }}
      >
        <thead
          style={{
            background: " #F9FAFB",
            fontFamily: "DM Sans",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: isSmallScreen ? "13px" : "16px",
            lineHeight: "20px",
            color: "#6C757D",
            marginBottom: "1rem !important",
            textTransform: "capitalize !important",
          }}
        >
          <tr>
            <td className="px-1 sm:px-2 py-2 sm:py-3 whitespace-nowrap" style={{ minWidth: widths.rank }}>#</td>
            <td className="px-1 sm:px-2 py-2 sm:py-3 whitespace-nowrap" style={{ minWidth: widths.player }}>Player</td>
            {bigMode && (
              <td className="px-1 sm:px-2 py-2 sm:py-3 whitespace-nowrap" style={{ minWidth: widths.workspace }}>
                Workspace
              </td>
            )}
            <td className="px-1 sm:px-2 py-2 sm:py-3 whitespace-nowrap" style={{ minWidth: widths.total }}>
              Total Points
            </td>
            {kpiHeaders.map((kpi) => (
              <td key={kpi.id} className="px-1 sm:px-2 py-2 sm:py-3 whitespace-nowrap" style={{ minWidth: widths.kpi }}>
                {kpi.label}
              </td>
            ))}
          </tr>
        </thead>
        <tbody className="text-center mb-2">
          {kpiScores.map((score, index) => {
            let badgeMaxWidth = "3rem";
            if (isSmallScreen) badgeMaxWidth = "2.25rem";
            else if (bigMode) badgeMaxWidth = "4rem";

            const badge =
              index < RANK_BADGES.length ? (
                <img
                  height="100%"
                  width="auto"
                  style={{ maxWidth: badgeMaxWidth, margin: "0 auto !important" }}
                  src={RANK_BADGES[index]}
                  alt="badgeRank"
                />
              ) : (
                <p className="font-semibold text-base sm:text-xl leading-tight sm:leading-[45.3px]">
                  {index + 1}
                </p>
              );

            const totalPoints = Math.round(score.scores?.total ?? 0);

            return (
              <tr
                key={score.userId}
                style={{
                  marginBottom: "1rem",
                  background: "#FFFFFF",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
                  fontFamily: "DM Sans",
                  fontStyle: "normal",
                  fontWeight: 500,
                  fontSize: isSmallScreen ? "13px" : "14px",
                  lineHeight: "20px",
                  color: "#343A40",
                }}
              >
                <td
                  className="px-1 sm:px-2 py-2 sm:py-3 flex items-center justify-center whitespace-nowrap"
                  style={{ minWidth: widths.rank }}
                >
                  {badge}
                </td>
                <td className="px-1 sm:px-2 py-2 sm:py-3 whitespace-nowrap" style={{ minWidth: widths.player }}>
                  <div className="flex items-center justify-start">
                    <SoftProfile
                      src={score.image}
                      size={bigMode ? "md" : "sm"}
                      borderRadius="lg"
                      name={score.firstName}
                      sx={{ mr: 1 }}
                    />
                    <div className="text-left">
                      <p className="font-semibold text-sm sm:text-md md:text-lg !leading-none">
                        {score.firstName}{" "}
                        {bigMode ? score.lastName : `${score.lastName?.charAt(0)}.`}
                      </p>
                    </div>
                  </div>
                </td>
                {bigMode && (
                  <td className="px-1 sm:px-2 py-2 sm:py-3 whitespace-nowrap" style={{ minWidth: widths.workspace }}>
                    <div className="flex items-center justify-start w-full">
                      <SoftProfile
                        src={score.group?.image && score.group.image !== score.image ? score.group.image : null}
                        size="md"
                        borderRadius="lg"
                        name={score.group?.name}
                        sx={{ mr: 1 }}
                      />
                      <div className="text-left">
                        <p className="font-semibold text-sm sm:text-md md:text-lg !leading-none">
                          {score.group?.name || "Ungrouped"}
                        </p>
                      </div>
                    </div>
                  </td>
                )}
                <td className="px-1 sm:px-2 py-2 sm:py-3 whitespace-nowrap" style={{ minWidth: widths.total }}>
                  <span style={valueTextSx(isSmallScreen, true)}>
                    {formatNumber(totalPoints, "#")}
                  </span>
                </td>
                {score.kpiAmounts?.map((kpi) => (
                  <td
                    key={String(kpi.id)}
                    className="px-1 sm:px-2 py-2 sm:py-3 whitespace-nowrap"
                    style={{ minWidth: widths.kpi }}
                  >
                    <span style={valueTextSx(isSmallScreen, false)}>
                      {kpi.kind === "%" ? formatKpiPercent(kpi, kpi.amt) : formatNumber(kpi.amt, kpi.kind)}
                    </span>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CompanyTopPerformers;
